import { HttpErrorResponse } from '@angular/common/http';
import { createFeature, createReducer, createSelector, on } from '@ngrx/store';
import { AuthPayloadTicket, AuthorizationData } from '@zero/shared/util-models';
import { CustomerAddressData } from '../../entities/customer-address-data.model';
import { KryptoOnboardingStatusData } from '../../entities/krypto-onboarding-status-data.model';
import { cryptoOnboardingActions, cryptoOnboardingApiActions } from './crypto-onboarding.actions';

interface CryptoOnboardingState {
  loading: boolean;
  status: KryptoOnboardingStatusData | null;
  customerAddress: CustomerAddressData | null;
  addressConfirmationDate: string | null;
  dataToAuthorize: AuthorizationData<AuthPayloadTicket> | null;
  authenticationFinished: boolean;
  error: HttpErrorResponse | null;
}

export const cryptoOnboardingState: CryptoOnboardingState = {
  loading: false,
  authenticationFinished: false,
  status: null,
  customerAddress: null,
  addressConfirmationDate: null,
  dataToAuthorize: null,
  error: null,
};

export const cryptoOnboardingFeature = createFeature({
  name: 'cryptoOnboarding',
  reducer: createReducer(
    cryptoOnboardingState,
    on(
      cryptoOnboardingActions.loadStatus,
      (state): CryptoOnboardingState => ({
        ...state,
        loading: true,
      }),
    ),
    on(
      cryptoOnboardingApiActions.loadStatusSuccess,
      (state, { status }): CryptoOnboardingState => ({
        ...state,
        status,
        loading: false,
      }),
    ),
    on(
      cryptoOnboardingActions.acceptConditions,
      (state): CryptoOnboardingState => ({
        ...state,
        loading: true,
        addressConfirmationDate: null,
        error: null,
      }),
    ),
    on(
      cryptoOnboardingApiActions.acceptConditionsSuccess,
      (state, { status }): CryptoOnboardingState => ({
        ...state,
        status,
        loading: false,
      }),
    ),
    on(
      cryptoOnboardingApiActions.acceptConditionsFailure,
      (state, { error }): CryptoOnboardingState => ({
        ...state,
        error,
        loading: false,
      }),
    ),
    on(
      cryptoOnboardingApiActions.loadCustomerAddressSuccess,
      (state, { address }): CryptoOnboardingState => ({
        ...state,
        customerAddress: address,
        loading: false,
      }),
    ),
    on(
      cryptoOnboardingApiActions.loadCustomerAddressFailure,
      (state): CryptoOnboardingState => ({
        ...state,
        loading: false,
      }),
    ),
    on(
      cryptoOnboardingActions.confirmAddress,
      (state): CryptoOnboardingState => ({
        ...state,
        addressConfirmationDate: new Date().toISOString(),
      }),
    ),
    on(
      cryptoOnboardingActions.resetAddressConfirmationDate,
      (state): CryptoOnboardingState => ({
        ...state,
        addressConfirmationDate: null,
      }),
    ),
    on(
      cryptoOnboardingActions.setDataToAuthorize,
      (state, { dataToAuthorize }): CryptoOnboardingState => ({
        ...state,
        dataToAuthorize,
      }),
    ),
    on(
      cryptoOnboardingActions.acceptContract,
      (state): CryptoOnboardingState => ({
        ...state,
        authenticationFinished: false,
        dataToAuthorize: null,
      }),
    ),
    on(cryptoOnboardingActions.handleAuthenticationResult, (state, { result }): CryptoOnboardingState => {
      if (result.success) {
        return { ...state, authenticationFinished: result.success, dataToAuthorize: null };
      }

      if (result.reason === 'CanceledByUser') {
        return { ...state, dataToAuthorize: null };
      }

      return state;
    }),
  ),
  extraSelectors: ({ selectAddressConfirmationDate }) => ({
    selectAddressConfirmed: createSelector(selectAddressConfirmationDate, (date) => !!date),
  }),
});
